/**
 * Wizard-Schritt 3: Kosten-Vorschau (estimate bei Mount/Änderung) + Start.
 * Baut channel_field_selections aus der Feld-Auswahl der gewählten Channels
 * (Spec 2026-07-24 §4).
 */

import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2 } from 'lucide-react'

import { EID } from '@/lib/element-ids.registry'
import type { ChannelFieldSelection } from '@/lib/api/channel-content-bulk'

import { useTextRunMutations } from '../hooks/useSuggestionMutations'
import { CostSummaryCard } from './CostSummaryCard'
import type { FieldSelections } from './useFieldSelections'

interface StepCostPreviewProps {
  productIds: string[]
  selectedChannelIds: string[]
  fieldSelections: FieldSelections
  researchEnabled: boolean
  onStarted: () => void
}

function toSelections(channelIds: string[], fieldSelections: FieldSelections): ChannelFieldSelection[] {
  return channelIds.flatMap((channelId) =>
    Object.entries(fieldSelections[channelId] ?? {})
      .filter(([, field]) => field.enabled)
      .map(([attributeCode, field]) => ({
        channel_id: channelId,
        attribute_code: attributeCode as ChannelFieldSelection['attribute_code'],
        template_id: field.templateId,
        system_tool_code: field.systemToolCode,
      })),
  )
}

export function StepCostPreview({
  productIds,
  selectedChannelIds,
  fieldSelections,
  researchEnabled,
  onStarted,
}: StepCostPreviewProps) {
  const { t } = useTranslation()
  const { estimate, start } = useTextRunMutations()
  const selections = toSelections(selectedChannelIds, fieldSelections)

  useEffect(() => {
    if (productIds.length === 0 || selections.length === 0) return
    estimate.mutate({ product_ids: productIds, channel_field_selections: selections, research_enabled: researchEnabled })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productIds.join(','), JSON.stringify(selections), researchEnabled])

  const handleStart = () => {
    start.mutate(
      { product_ids: productIds, channel_field_selections: selections, research_enabled: researchEnabled },
      { onSuccess: (res) => { if (res.success) onStarted() } },
    )
  }

  if (estimate.isPending) {
    return (
      <div className="flex items-center gap-2 rounded-lg bg-neutral-100 p-4 text-sm text-neutral-500 dark:bg-neutral-800 dark:text-neutral-400">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        {t('channelContent.wizard.costLoading')}
      </div>
    )
  }

  if (estimate.isError) {
    return (
      <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-800 dark:bg-red-950/30 dark:text-red-300">
        {t('channelContent.wizard.previewError')}
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <CostSummaryCard
        productCount={productIds.length}
        channelCount={selectedChannelIds.length}
        researchEnabled={researchEnabled}
        result={estimate.data}
      />
      <button
        type="button"
        id={EID['text-bulk-wizard-start-btn']}
        onClick={handleStart}
        disabled={start.isPending || selections.length === 0}
        className="w-full rounded-md bg-orange-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
      >
        {t('channelContent.wizard.start')}
      </button>
    </div>
  )
}
